<template>
  <div>
    <Teleport to="[data-header-action]" v-if="isMounted">
      <div class="[ flex items-center gap-4 ]">
        <DateRangeRadio 
          :range-options="feedbackDateRanges" 
          :intervals="feedbackDateRangeIntervals"
          :show-custom-datepicker="false"
          :show-calendar-button="false"
          :custom-date-range="customDateRange"
          v-model="dateRangeIndex" 
          @custom-date-selected="setCustomDateFilter" 
        />
        <Button 
          label="Export CSV" 
          severity="secondary" 
          outlined
          :loading="exporting"
          :disabled="exporting || loading"
          @click="exportCSV"
        >
          <template #icon>
            <JBIcon name="download" class="[ w-4 h-4 ]" />
          </template>
        </Button>
      </div>
    </Teleport>
    <DataTable
      :value="feedbackData"
      :loading="loading"
      :rows="filters.pageSize"
      :total-records="totalRecords"
      lazy
      paginator
      :rows-per-page-options="[10, 20, 50, 100]"
      @page="onPage"
      @sort="onSort"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      scrollable
      scroll-height="flex"
      selection-mode="single"
      @row-select="onRowSelect"
      :row-class="() => '[ cursor-pointer hover:bg-gray-50 ]'"
    >
      <template #empty>
        <div class="[ flex flex-col items-center justify-center py-12 ]">
          <JBIcon name="message" class="[ w-16 h-16 text-gray-400 mb-4 ]" />
          <h3 class="[ font-semibold text-lg mb-2 ]">No feedback found</h3>
          <p class="[ text-gray-dark ]">
            Try adjusting your date range
          </p>
        </div>
      </template>

      <Column field="id" header="ID" body-class="[ align-top ]">
        <template #body="{ data }">
          <span class="[ font-mono text-sm ]">{{ data.id }}</span>
        </template>
      </Column>

      <Column field="dateCreated" header="Date & Time" body-class="[ align-top ]" class="[ min-w-52 ]" sortable>
        <template #body="{ data }">
          <div class="[ flex gap-3 text-gray-dark ]">
            <span class="[ border-r pr-3 ]">
              {{ formatDate(data.dateCreated) }}
            </span>
            <span>
              {{ formatTime(data.dateCreated) }}
            </span>
          </div>
        </template>
      </Column>

      <Column field="accountId" header="Reported By" body-class="[ align-top ]" class="[ min-w-40 ]">
        <template #body="{ data }">
          <span class="[ text-sm ]">{{ getReporterName(data) }}</span>
        </template>
      </Column>

      <Column field="scorecardId" header="Scorecard" body-class="[ align-top ]" sortable>
        <template #body="{ data }">
          <a
            v-if="data.scorecardId"
            :href="getScorecardUrl(data.scorecardId, data.content)"
            target="_blank"
            rel="noopener noreferrer"
            class="[ text-highlight hover:underline ]"
          >
            {{ data.scorecardId }}
          </a>
          <span v-else class="[ text-gray-dark ]">-</span>
        </template>
      </Column>

      <Column field="context" header="Context" body-class="[ align-top ]">
        <template #body="{ data }">
          <div v-if="hasContextData(data.content)" class="[ flex gap-2 flex-wrap ]">
            <div
              v-if="data.content?.ruleId"
              class="[ flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded text-blue-800 ]"
            >
              <JBIcon name="target" class="[ w-3.5 h-3.5 ]" />
              <span class="[ text-xs font-medium ]">Rule {{ data.content.ruleId }}</span>
            </div>
            <div
              v-if="data.content?.utteranceId"
              class="[ flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 border border-purple-200 rounded text-purple-800 ]"
            >
              <JBIcon name="message" class="[ w-3.5 h-3.5 ]" />
              <span class="[ text-xs font-medium ]">Utterance {{ data.content.utteranceId }}</span>
            </div>
            <div
              v-if="data.content?.sentenceId"
              class="[ flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded text-green-800 ]"
            >
              <JBIcon name="label" class="[ w-3.5 h-3.5 ]" />
              <span class="[ text-xs font-medium ]">Sentence {{ data.content.sentenceId }}</span>
            </div>
            <div
              v-if="data.content?.ledgerItemIndex !== undefined"
              class="[ flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded text-amber-800 ]"
            >
              <JBIcon name="layers" class="[ w-3.5 h-3.5 ]" />
              <span class="[ text-xs font-medium ]">Ledger Item {{ data.content.ledgerItemIndex }}</span>
            </div>
          </div>
          <span v-else class="[ text-gray-dark ]">-</span>
        </template>
      </Column>

      <Column field="content" header="Feedback" body-class="[ align-top ]" class="[ min-w-96 ]">
        <template #body="{ data }">
          <div v-if="data.content?.text" class="[ text-sm whitespace-pre-wrap ]">
            {{ data.content.text }}
          </div>
          <div v-else class="[ text-gray-dark italic ]">No text content</div>
        </template>
      </Column>

    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { format, startOfToday, endOfToday, endOfDay, startOfDay, subDays, subMonths, subYears } from "date-fns";
import {
  DataTable,
  Column,
  Button,
} from "primevue";
import type { DataTablePageEvent, DataTableSortEvent } from "primevue";
import { useToast } from "primevue";
import JBIcon from "@/components/JBIcon.vue";
import DateRangeRadio from "@/components/contact/qa/DateRangeRadio.vue";
import { useApi } from "@/services/api";
import {
  FeedbackSortBy,
  type ScorecardFeedback,
  type ScorecardFeedbackContent,
} from "@/services/api/sdk";

definePage({
  name: "feedback",
  meta: {
    icon: "message",
    title: "Scorecard Feedback",
    scope: "Scorecard.Read",
    noPadding: true,
  },
});

const router = useRouter();
const toast = useToast();
const api = useApi();

// Custom date ranges for feedback page: Today, Yesterday, 1W, 1M, 3M, All
const now = new Date();
const feedbackDateRanges: [Date | null, Date | null][] = [
  [startOfToday(), endOfToday()],                    // Today
  [startOfDay(subDays(now, 1)), endOfDay(subDays(now, 1))],  // Yesterday
  [startOfDay(subDays(now, 7)), now],                // 1 Week
  [startOfDay(subMonths(now, 1)), now],              // 1 Month
  [startOfDay(subMonths(now, 3)), now],              // 3 Months
  [startOfDay(subYears(now, 10)), now],              // All (10 years back)
];

const feedbackDateRangeIntervals: string[] = [
  "Today",
  "Yesterday", 
  "1W",
  "1M",
  "3M",
  "All",
];

// Mount state for Teleport
const isMounted = ref(false);

// Date range state
const dateRangeIndex = ref(0);
const customDateRange = ref<[Date | null, Date | null]>([null, null]);

const dateRange = computed(() => {
  return feedbackDateRanges[dateRangeIndex.value];
});

function setCustomDateFilter(
  v: Date | (Date | null)[] | Date[] | null | undefined,
) {
  // Not used anymore, but keeping for component compatibility
  customDateRange.value = v as [Date, Date];
}

// State
const loading = ref(false);
const exporting = ref(false);
const feedbackData = ref<ScorecardFeedback[]>([]);
const totalRecords = ref(0);
const totalPages = ref(0);
const currentPage = ref(1);
const sortField = ref<string>("dateCreated");
const sortOrder = ref<number>(-1); // -1 for descending, 1 for ascending

// Filters
const filters = ref<{
  sortBy: FeedbackSortBy;
  desc: boolean;
  filterDateMin: Date | undefined;
  filterDateMax: Date | undefined;
  page: number;
  pageSize: number;
}>({
  sortBy: FeedbackSortBy.DateCreated,
  desc: true,
  filterDateMin: undefined,
  filterDateMax: undefined,
  page: 1,
  pageSize: 20,
});

// Watch date range changes
watch(dateRange, (range) => {
  const [start, end] = range;
  if (start && end) {
    filters.value.filterDateMin = start;
    filters.value.filterDateMax = end;
    filters.value.page = 1; // Reset to first page
    fetchFeedback();
  }
}, { immediate: true });

// Methods
function getReporterName(feedback: ScorecardFeedback): string {
  const name = [feedback.accountFirstName, feedback.accountLastName].filter(Boolean).join(" ");
  return name || (feedback.accountId ? `User ${feedback.accountId}` : "-");
}

async function fetchFeedback() {
  loading.value = true;
  try {
    const response = await api.getAllScorecardFeedbacks({
      sortBy: filters.value.sortBy,
      desc: filters.value.desc,
      page: filters.value.page,
      pageSize: filters.value.pageSize,
      filterDateMin: filters.value.filterDateMin,
      filterDateMax: filters.value.filterDateMax,
    });

    if (response.errors && response.errors.length > 0) {
      console.error("API errors:", response.errors);
      toast.add({
        severity: "warn",
        summary: "Partial Results",
        detail: "Some errors occurred while fetching feedback",
        life: 3000,
      });
    }

    feedbackData.value = response.data || [];
    totalRecords.value = response.totalRecords || 0;
    totalPages.value = response.pages || 0;
    currentPage.value = response.page || 1;
  } catch (error) {
    console.error("Failed to fetch feedback:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: error instanceof Error ? error.message : "Failed to load feedback data",
      life: 5000,
    });
    feedbackData.value = [];
    totalRecords.value = 0;
    totalPages.value = 0;
  } finally {
    loading.value = false;
  }
}

async function exportCSV() {
  exporting.value = true;
  try {
    const [start, end] = dateRange.value;
    
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (filters.value.sortBy) {
      queryParams.set('sortBy', filters.value.sortBy);
    }
    if (filters.value.desc !== undefined) {
      queryParams.set('desc', String(filters.value.desc));
    }
    if (start) {
      queryParams.set('filterDateMin', start.toISOString());
    }
    if (end) {
      queryParams.set('filterDateMax', end.toISOString());
    }
    
    // Get auth token
    const { useAuthStore } = await import('@/store/auth');
    const authStore = useAuthStore();
    const { useClientConfig } = await import('@/store/client-config');
    const clientConfig = useClientConfig();
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${authStore.bearerToken}`,
    };
    
    if (clientConfig.selectedTenant !== -1) {
      headers['X-Tenant-Id'] = clientConfig.selectedTenant.toString();
    }
    
    const baseUrl = import.meta.env.VITE_API_URL;
    const response = await fetch(
      `${baseUrl}/app/scorecards/feedback/export?${queryParams.toString()}`,
      { headers }
    );
    
    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }
    
    // Get CSV as text and enhance it
    const csvText = await response.text();
    const enhancedCsv = enhanceCsvWithUserNamesAndLinks(csvText);
    
    // Generate filename with date range
    const intervalLabel = feedbackDateRangeIntervals[dateRangeIndex.value];
    let fileName: string;
    
    if (intervalLabel === 'Today' || intervalLabel === 'Yesterday') {
      // Single date
      const dateStr = start ? format(start, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
      fileName = `feedback_${intervalLabel.toLowerCase()}_${dateStr}.csv`;
    } else if (start && end) {
      // Date range
      const startStr = format(start, 'yyyy-MM-dd');
      const endStr = format(end, 'yyyy-MM-dd');
      fileName = `feedback_${startStr}_to_${endStr}.csv`;
    } else {
      fileName = `feedback_export_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    }
    
    // Download the enhanced file with UTF-8 BOM for proper Excel encoding detection
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + enhancedCsv], { type: 'text/csv;charset=utf-8;' });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the blob URL
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    
    toast.add({
      severity: 'success',
      summary: 'Export Complete',
      detail: `Downloaded ${fileName}`,
      life: 3000,
    });
  } catch (error) {
    console.error('Failed to export feedback:', error);
    toast.add({
      severity: 'error',
      summary: 'Export Failed',
      detail: error instanceof Error ? error.message : 'Failed to export feedback data',
      life: 5000,
    });
  } finally {
    exporting.value = false;
  }
}

function enhanceCsvWithUserNamesAndLinks(csvText: string): string {
  // Parse CSV properly, handling multi-line quoted fields
  const rows = parseCSV(csvText);
  if (rows.length === 0) return csvText;
  
  // Parse header to find column indices
  const headers = rows[0];
  
  const accountIdIndex = headers.findIndex(h => h.toLowerCase() === 'accountid');
  const scorecardIdIndex = headers.findIndex(h => h.toLowerCase() === 'scorecardid');
  
  // Build a lookup map from loaded feedback data for reporter names
  const reporterNameMap = new Map<number, string>();
  for (const feedback of feedbackData.value) {
    if (feedback.accountId) {
      const name = [feedback.accountFirstName, feedback.accountLastName].filter(Boolean).join(" ");
      reporterNameMap.set(feedback.accountId, name || `User ${feedback.accountId}`);
    }
  }
  
  // Build the base URL for scorecard links
  const appBaseUrl = window.location.origin;
  
  // Process header - add "Reported By" column after AccountId, rename ScorecardId to include link info
  const newHeaders = [...headers];
  if (accountIdIndex !== -1) {
    newHeaders.splice(accountIdIndex + 1, 0, 'Reported By');
  }
  if (scorecardIdIndex !== -1) {
    const adjustedIndex = accountIdIndex !== -1 && scorecardIdIndex > accountIdIndex 
      ? scorecardIdIndex + 1 
      : scorecardIdIndex;
    newHeaders[adjustedIndex] = 'Scorecard Link';
  }
  
  const resultLines: string[] = [newHeaders.map(h => escapeCSVField(h)).join(',')];
  
  // Process data rows
  for (let i = 1; i < rows.length; i++) {
    const fields = rows[i];
    if (fields.length === 0 || (fields.length === 1 && !fields[0])) continue;
    
    const newFields = [...fields];
    
    // Add user name after accountId
    if (accountIdIndex !== -1 && accountIdIndex < fields.length) {
      const accountId = parseInt(fields[accountIdIndex], 10);
      const userName = reporterNameMap.get(accountId) || `User ${accountId}`;
      newFields.splice(accountIdIndex + 1, 0, userName);
    }
    
    // Convert scorecardId to hyperlink
    if (scorecardIdIndex !== -1) {
      const adjustedIndex = accountIdIndex !== -1 && scorecardIdIndex > accountIdIndex 
        ? scorecardIdIndex + 1 
        : scorecardIdIndex;
      
      if (adjustedIndex < newFields.length) {
        const scorecardId = newFields[adjustedIndex];
        if (scorecardId && scorecardId !== '-') {
          const url = `${appBaseUrl}/scorecards/${scorecardId}/result`;
          // Use HYPERLINK formula for Excel/Google Sheets compatibility
          newFields[adjustedIndex] = `=HYPERLINK("${url}","${scorecardId}")`;
        }
      }
    }
    
    resultLines.push(newFields.map(f => escapeCSVField(f)).join(','));
  }
  
  return resultLines.join('\n');
}

/**
 * Parse CSV text into rows, properly handling:
 * - Quoted fields with embedded newlines
 * - Escaped quotes (doubled quotes)
 * - Mixed line endings (CRLF, LF, CR)
 */
function parseCSV(csvText: string): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;
  
  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];
    
    if (inQuotes) {
      if (char === '"') {
        if (nextChar === '"') {
          // Escaped quote - add single quote and skip next
          currentField += '"';
          i++;
        } else {
          // End of quoted field
          inQuotes = false;
        }
      } else {
        // Inside quoted field - add character as-is
        currentField += char;
      }
    } else {
      if (char === '"') {
        // Start of quoted field
        inQuotes = true;
      } else if (char === ',') {
        // Field delimiter
        currentRow.push(currentField);
        currentField = '';
      } else if (char === '\r' && nextChar === '\n') {
        // Windows line ending - end of row
        currentRow.push(currentField);
        rows.push(currentRow);
        currentRow = [];
        currentField = '';
        i++; // Skip the \n
      } else if (char === '\n' || char === '\r') {
        // Unix or old Mac line ending - end of row
        currentRow.push(currentField);
        rows.push(currentRow);
        currentRow = [];
        currentField = '';
      } else {
        currentField += char;
      }
    }
  }
  
  // Don't forget the last field and row
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField);
    rows.push(currentRow);
  }
  
  return rows;
}

function escapeCSVField(field: string): string {
  // First, normalize and replace line breaks with a space
  // This prevents issues with embedded newlines breaking CSV structure
  let sanitized = field
    .replace(/\r\n/g, ' ')  // Windows line endings
    .replace(/\r/g, ' ')     // Old Mac line endings
    .replace(/\n/g, ' ')     // Unix line endings
    .replace(/\s+/g, ' ')    // Collapse multiple whitespace
    .trim();
  
  // Check if field needs quoting:
  // - Contains comma (delimiter)
  // - Contains double quote
  // - Starts with formula characters (Excel formula injection prevention)
  const needsQuoting = 
    sanitized.includes(',') || 
    sanitized.includes('"') ||
    /^[=+\-@]/.test(sanitized);
  
  if (needsQuoting) {
    // Escape double quotes by doubling them, then wrap in quotes
    return `"${sanitized.replace(/"/g, '""')}"`;
  }
  return sanitized;
}

function updateSortState() {
  // Map FeedbackSortBy enum to field names for DataTable
  const fieldMap: Record<FeedbackSortBy, string> = {
    [FeedbackSortBy.DateCreated]: "dateCreated",
    [FeedbackSortBy.ScorecardId]: "scorecardId",
    [FeedbackSortBy.AccountId]: "", // Not displayed but needs to be in the map
  };
  
  sortField.value = fieldMap[filters.value.sortBy];
  sortOrder.value = filters.value.desc ? -1 : 1;
}

function onPage(event: DataTablePageEvent) {
  filters.value.page = (event.page || 0) + 1; // PrimeVue uses 0-based pages
  filters.value.pageSize = event.rows || 20;
  fetchFeedback();
}

function onSort(event: DataTableSortEvent) {
  // Map DataTable field names to FeedbackSortBy enum
  const fieldMap: Record<string, FeedbackSortBy> = {
    dateCreated: FeedbackSortBy.DateCreated,
    scorecardId: FeedbackSortBy.ScorecardId,
  };

  const mappedField = fieldMap[event.sortField as string];
  if (mappedField) {
    filters.value.sortBy = mappedField;
    filters.value.desc = event.sortOrder === -1;
    filters.value.page = 1; // Reset to first page when sorting changes
    fetchFeedback();
  }
}

function onRowSelect(event: any) {
  const feedback = event.data as ScorecardFeedback;
  if (feedback.scorecardId) {
    // Open scorecard in new window
    window.open(getScorecardUrl(feedback.scorecardId, feedback.content), '_blank');
  }
}

function getScorecardUrl(scorecardId: number, content?: ScorecardFeedbackContent): string {
  const query: Record<string, string> = {};
  
  // Add context parameters to jump to specific item
  if (content) {
    if (content.ruleId !== undefined) {
      query.ruleId = content.ruleId.toString();
    }
    if (content.utteranceId !== undefined) {
      query.utteranceId = content.utteranceId.toString();
    }
    if (content.sentenceId !== undefined) {
      query.sentenceId = content.sentenceId.toString();
    }
    if (content.ledgerItemIndex !== undefined) {
      query.ledgerItemIndex = content.ledgerItemIndex.toString();
    }
  }
  
  const route = router.resolve({
    name: 'scorecard-result',
    params: { id: scorecardId.toString() },
    query: query,
  });
  return route.href;
}

function formatDate(date: Date | undefined): string {
  if (!date) return "";
  try {
    return format(date, "MMM dd, yyyy");
  } catch (error) {
    return "";
  }
}

function formatTime(date: Date | undefined): string {
  if (!date) return "";
  try {
    return format(date, "h:mm a");
  } catch (error) {
    return "";
  }
}

function hasContextData(content?: ScorecardFeedbackContent): boolean {
  if (!content) return false;
  return !!(
    content.ruleId ||
    content.utteranceId ||
    content.sentenceId ||
    content.ledgerItemIndex !== undefined
  );
}

// Lifecycle
onMounted(() => {
  isMounted.value = true;
  // Date range watcher will trigger initial load
  updateSortState();
});
</script>

<style scoped>
/* Custom styles if needed */
</style>

